// #region Imports
import { enqueueSnackbar } from "notistack";
import { StaffingCandidate } from "work/staffing/_api/work-staffing-api";
import { StaffingDestinationKind } from "work/staffing/_schemas/staffing-destination/staffing-destination-schema";
import { StaffingOutcomeJSON } from "work/staffing/_schemas/staffing-outcome/staffing-outcome-schema";
import StaffingDestination from "work/staffing/staffing-destination";
// #endregion

/**
 * What happens after somebody picks a destination, and what the viewer is told about it.
 *
 * Its own module because both halves of the feature need it — the header button owns the mutation
 * and the picker owns the press — and because a file importing the other would close the loop. It
 * is also all module-scope functions on purpose: the React Compiler bails out of any component
 * containing the `try`/`catch` below, and a bail-out there would cost the profile header its
 * memoization. See the React Compiler section of `app/CLAUDE.md`.
 */

/** What the viewer is told when somebody was seated. */
export const addedToWorkMessage = (workName: string): string => `Added to ${workName}.`;

/** What the viewer is told when nothing happened because the person was already there. */
export const alreadyOnWorkMessage = (workName: string): string => `Already on ${workName}.`;

/** What the viewer is told when the roster turned out to be a term both parties agreed. */
export const revisionOpenedMessage = (workName: string): string =>
  `The team on ${workName} is an agreed term, so a revision was opened to change it. ` +
  "Nobody has been added yet.";

/** The fallback when the API refuses without saying why. */
export const ADD_TO_WORK_REFUSED = "Could not add this person to that work.";

/** Where the negotiation a revision opened is read. */
export const revisionRoute = (revisionProposalId: string): string =>
  `/proposals/active/revise/${revisionProposalId}/details`;

type ModelStateErrors = Readonly<Record<string, readonly string[]>>;

/**
 * The reason the API gave for refusing, in the viewer's language.
 *
 * The endpoint reports a refusal it can explain as a 400 carrying model-state errors, and those
 * sentences are written for the person reading them. Anything else is a fault, not an explanation,
 * and gets the fallback.
 */
export function staffingRefusalMessage(error: unknown): string {
  if (!error || typeof error !== "object" || !("data" in error)) return ADD_TO_WORK_REFUSED;
  const data = (error as { data?: unknown }).data;
  if (!data || typeof data !== "object") return ADD_TO_WORK_REFUSED;
  const sentences = Object.values(data as ModelStateErrors)
    .flatMap((value) => (Array.isArray(value) ? value : []))
    .filter((value): value is string => typeof value === "string" && value.trim().length > 0);
  return sentences.length > 0 ? sentences[0] : ADD_TO_WORK_REFUSED;
}

/**
 * Seats the candidate, says what happened, and takes the viewer to the revision when there is one.
 *
 * Nothing here decides anything: the row's already-on-the-team flag can be a moment out of date,
 * so what the viewer reads is what the server answered rather than what the row said.
 */
export async function addToWork(
  addToStaffingDestination: (args: {
    kind: StaffingDestinationKind;
    destinationId: string;
    candidate: StaffingCandidate;
  }) => { unwrap: () => Promise<StaffingOutcomeJSON> },
  candidate: StaffingCandidate,
  destination: StaffingDestination,
  navigate: (to: string) => void,
): Promise<void> {
  try {
    const outcome = await addToStaffingDestination({
      kind: destination.kind,
      destinationId: destination.id.value,
      candidate,
    }).unwrap();

    if (outcome.alreadyPresent) {
      enqueueSnackbar(alreadyOnWorkMessage(outcome.name), { variant: "info" });
      return;
    }
    if (outcome.revisionProposalId) {
      // Nobody was seated. Saying so and then landing the viewer on the negotiation is the same
      // thing the revise action on the agreement itself does.
      enqueueSnackbar(revisionOpenedMessage(outcome.name), { variant: "warning" });
      navigate(revisionRoute(outcome.revisionProposalId));
      return;
    }
    enqueueSnackbar(addedToWorkMessage(outcome.name), { variant: "success" });
  } catch (error) {
    enqueueSnackbar(staffingRefusalMessage(error), { variant: "error" });
  }
}
